import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Heart, MessageCircle, Bell, UserPlus, Send } from 'lucide-react';

const formatTimeAgo = (iso, t) => {
  if (!iso) return '';
  const created = new Date(iso);
  const diffMs = Date.now() - created.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return t('justNow');
  if (minutes < 60) return t('minutesAgo').replace('{n}', minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('hoursAgo').replace('{n}', hours);
  const days = Math.floor(hours / 24);
  return t('daysAgo').replace('{n}', days);
};

const Notifications = ({ token, API, t, isRTL, onOpenProfile, onAfterRead, liveEvent }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/notifications?limit=50`, { headers });
      setItems(res.data.notifications || []);

      // Auto mark all as read on tab open (server-side persistence)
      if ((res.data.unread_count || 0) > 0) {
        await axios.post(`${API}/notifications/read`, {}, { headers });
        if (onAfterRead) onAfterRead();
      }
    } catch (e) {
      console.error('Notifications load error', e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, API, onAfterRead]);

  useEffect(() => {
    load();
  }, [load]);

  // Phase 19 — react to live SSE pushes while the screen is open.
  // Prepend the new notification (deduped by id) so the user sees it
  // arrive without waiting for a fetch. Also flag it as read since
  // this screen auto-marks everything read on open.
  useEffect(() => {
    if (!liveEvent || liveEvent.type !== 'notification') return;
    const incoming = liveEvent.data;
    if (!incoming?.id) return;
    setItems((prev) => {
      if (prev.some((n) => n.id === incoming.id)) return prev;
      return [{ ...incoming, is_read: true }, ...prev];
    });
    // Persist the read state on the server so the badge stays at 0
    // while this tab remains visible.
    const headers = { Authorization: `Bearer ${token}` };
    axios.post(`${API}/notifications/read`, {}, { headers }).catch(() => {});
  }, [liveEvent, token, API]);

  const markAllRead = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/notifications/read`, {}, { headers });
      // visually clear unread state
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
      if (onAfterRead) onAfterRead();
    } catch (e) {
      // no-op
    }
  };

  if (loading) {
    return (
      <div
        data-testid="notifications-loading"
        className="flex items-center justify-center py-20 text-gray-500"
      >
        {t('loading')}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="notifications-empty"
        dir={isRTL ? 'rtl' : 'ltr'}
        className="max-w-xl mx-auto px-6 pt-16 pb-6 text-center"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-purple-100 flex items-center justify-center text-purple-600 mb-4">
          <Bell className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">{t('activityEmpty')}</h2>
        <p className="text-sm text-gray-500">{t('activityEmptyHint')}</p>
      </div>
    );
  }

  const hasUnread = items.some((n) => !n.is_read);

  return (
    <div
      data-testid="notifications-screen"
      dir={isRTL ? 'rtl' : 'ltr'}
      className="max-w-xl mx-auto px-3 pt-3 pb-6"
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-lg font-bold text-gray-900">{t('notifications')}</h2>
        {hasUnread && (
          <button
            onClick={markAllRead}
            data-testid="notifications-mark-all-read"
            className="text-xs font-medium text-purple-600 hover:text-purple-700"
          >
            {t('markAllRead')}
          </button>
        )}
      </div>

      <ul className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
        {items.map((n) => {
          const isLike = n.type === 'like';
          const isFollow = n.type === 'follow';
          const isMessage = n.type === 'message' || n.type === 'message_request';
          const Icon = isLike ? Heart : isFollow ? UserPlus : isMessage ? Send : MessageCircle;
          const verbKey = isLike
            ? 'activityLikedYourPost'
            : isFollow
            ? 'activityFollowedYou'
            : n.type === 'message'
            ? 'activityNewMessage'
            : n.type === 'message_request'
            ? 'activityMessageRequest'
            : 'activityCommentedOnYourPost';
          return (
            <li
              key={n.id}
              data-testid={`notification-${n.id}`}
              className={`flex items-start gap-3 px-3 py-3 ${
                n.is_read ? 'bg-white' : 'bg-purple-50/60'
              }`}
            >
              <button
                onClick={() => onOpenProfile && onOpenProfile(n.actor_user_id)}
                className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold"
                data-testid={`notification-actor-${n.id}`}
              >
                {(n.actor_name || '?').charAt(0).toUpperCase()}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">
                  <span className="font-semibold">{n.actor_name}</span>{' '}
                  <span className="text-gray-700">{t(verbKey)}</span>
                  {!n.is_read && (
                    <span
                      data-testid={`notification-unread-dot-${n.id}`}
                      className="inline-block ms-2 w-2 h-2 rounded-full bg-purple-600 align-middle"
                    />
                  )}
                </p>
                {n.type === 'comment' && n.comment_text && (
                  <p className="text-sm text-gray-500 truncate mt-0.5">
                    “{n.comment_text}”
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {formatTimeAgo(n.created_at, t)}
                </p>
              </div>
              <Icon
                className={`w-4 h-4 mt-1 shrink-0 ${
                  isLike
                    ? 'text-red-500'
                    : isFollow
                    ? 'text-emerald-500'
                    : isMessage
                    ? 'text-sky-500'
                    : 'text-purple-500'
                }`}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Notifications;
